// frontend/src/services/glApi.js
import axios from "axios";

/**
 * Golden Lines Manager API service.
 */

const pickBaseUrl = () => {
  const envBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "";

  const raw = String(envBase || "").trim();

  if (!raw) return "/api";

  let s = raw.replace(/\/+$/, "");

  if (s === "/api" || s.toLowerCase() === "api") return "/api";

  s = s.replace(/\/api$/i, "");

  if (!s) return "/api";

  if (/^https?:\/\//i.test(s)) return `${s}/api`;

  return s.startsWith("/") ? s : `/${s}`;
};

const BASE_URL = pickBaseUrl();

let loggedBase = false;

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 600000,
  withCredentials: false,
});

const sanitizeId = (value) => {
  if (value === undefined || value === null) return "";

  let s = String(value).trim();

  try {
    s = decodeURIComponent(s);
  } catch (_err) {}

  return s.replace(/^<+/, "").replace(/>+$/, "").trim();
};

const cleanParams = (params = {}) => {
  const out = {};

  for (const [key, value] of Object.entries(params || {})) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.trim() === "") continue;

    out[key] = value;
  }

  return out;
};

const withNoCache = (params = {}) =>
  cleanParams({
    ...params,
    _cb: Date.now(),
  });

const getNormalizedPath = (configUrl = "") => {
  const raw = String(configUrl || "").trim();

  if (/^https?:\/\//i.test(raw)) {
    try {
      const url = new URL(raw);
      return url.pathname || "/";
    } catch (_err) {
      return "/";
    }
  }

  return raw.startsWith("/") ? raw : `/${raw}`;
};

const stripApiPrefix = (pathname = "") =>
  String(pathname || "").replace(/^\/api\b/i, "") || "/";

api.interceptors.request.use((config) => {
  if (!loggedBase) {
    console.log("[GL API BASE_URL]", BASE_URL);
    loggedBase = true;
  }

  config.headers = config.headers || {};

  const pathname = getNormalizedPath(config.url);
  const cleanPath = stripApiPrefix(pathname);

  const isPublic =
    cleanPath === "/gl-auth/login" ||
    cleanPath === "/gl-auth/logout";

  if (isPublic) {
    delete config.headers.Authorization;
    delete config.headers["X-GL-Portal"];
    return config;
  }

  const token = localStorage.getItem("glToken") || "";

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    config.headers["X-GL-Portal"] = "manager";
  } else {
    delete config.headers.Authorization;
    delete config.headers["X-GL-Portal"];
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url;
    const data = error?.response?.data;

    console.warn("[GL API ERROR]", status, url, data || error?.message);

    return Promise.reject(error);
  }
);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withRetry429 = async (fn, options = {}) => {
  const retries = options.retries ?? 2;
  const delayMs = options.delayMs ?? 900;

  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (error?.response?.status !== 429 || attempt === retries) {
        throw error;
      }

      const retryAfterHeader = error?.response?.headers?.["retry-after"];
      const retryAfterMs = retryAfterHeader
        ? Number(retryAfterHeader) * 1000
        : delayMs * (attempt + 1);

      await sleep(Number.isFinite(retryAfterMs) ? retryAfterMs : delayMs);
    }
  }

  throw lastError;
};

/* ========================= GL AUTH ========================= */

export const glAuthAPI = {
  me: () => api.get('/gl-auth/me'),
  changePassword: ({ currentPassword, newPassword }) =>
    api.post('/gl-auth/change-password', { currentPassword, newPassword }),
  login: (emailOrPayload = {}, maybePassword = "") => {
    let email = "";
    let password = "";

    if (
      emailOrPayload &&
      typeof emailOrPayload === "object" &&
      !Array.isArray(emailOrPayload)
    ) {
      email =
        emailOrPayload.email ||
        emailOrPayload.emailAddress ||
        emailOrPayload.username ||
        "";

      password =
        emailOrPayload.password ||
        emailOrPayload.pass ||
        maybePassword ||
        "";
    } else {
      email = emailOrPayload || "";
      password = maybePassword || "";
    }

    email = String(email || "").trim();
    password = String(password || "");

    return withRetry429(
      () =>
        api.post("/gl-auth/login", {
          email,
          password,
        }),
      {
        retries: 1,
        delayMs: 1000,
      }
    );
  },

  logout: async () => {
    try {
      await api.post("/gl-auth/logout");
    } catch (_err) {}

    localStorage.removeItem("glToken");
    localStorage.removeItem("glUser");

    return {
      status: "success",
    };
  },
};

const directorApi = axios.create({
  baseURL: BASE_URL,
  timeout: 600000,
  withCredentials: false,
});

const safelyReadJson = (storage, key) => {
  try {
    const raw = storage.getItem(key);

    if (!raw) {
      return null;
    }

    return JSON.parse(raw);
  } catch (_error) {
    return null;
  }
};

const tokenFromObject = (value) => {
  if (!value || typeof value !== "object") {
    return "";
  }

  return (
    value.token ||
    value.authToken ||
    value.accessToken ||
    value.jwt ||
    value.data?.token ||
    value.data?.authToken ||
    value.data?.accessToken ||
    value.data?.jwt ||
    value.user?.token ||
    value.user?.authToken ||
    value.user?.accessToken ||
    value.user?.jwt ||
    ""
  );
};

const getDirectorToken = () => {
  const directToken =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("jwt") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("jwt") ||
    "";

  if (directToken) {
    return String(directToken).trim();
  }

  const localKeys = [
    "auth",
    "userData",
    "user",
    "authUser",
    "currentUser",
    "vfUser",
  ];

  for (const key of localKeys) {
    const token = tokenFromObject(safelyReadJson(localStorage, key));

    if (token) {
      return String(token).trim();
    }
  }

  const sessionKeys = [
    "auth",
    "userData",
    "user",
    "authUser",
    "currentUser",
    "vfUser",
  ];

  for (const key of sessionKeys) {
    const token = tokenFromObject(safelyReadJson(sessionStorage, key));

    if (token) {
      return String(token).trim();
    }
  }

  return "";
};

directorApi.interceptors.request.use((config) => {
  config.headers = config.headers || {};

  const token = getDirectorToken();

  /*
   * This header tells the shared Golden Lines backend routes
   * to validate the request with the normal Valli auth middleware.
   */
  config.headers["X-GL-Portal"] = "directors";

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;

    console.warn(
      "[GL DIRECTORS API] No directors authentication token was found.",
      {
        availableStorageKeys: {
          token: Boolean(localStorage.getItem("token")),
          authToken: Boolean(localStorage.getItem("authToken")),
          accessToken: Boolean(localStorage.getItem("accessToken")),
          jwt: Boolean(localStorage.getItem("jwt")),
          userData: Boolean(localStorage.getItem("userData")),
          user: Boolean(localStorage.getItem("user")),
          auth: Boolean(localStorage.getItem("auth")),
        },
      }
    );
  }

  if (process.env.NODE_ENV === "development") {
    console.log("[GL DIRECTORS REQUEST]", {
      baseURL: BASE_URL,
      url: config.url,
      portal: config.headers["X-GL-Portal"],
      hasToken: Boolean(token),
    });
  }

  return config;
});
directorApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.warn(
      "[GL DIRECTORS API ERROR]",
      error?.response?.status,
      error?.config?.url,
      error?.response?.data || error?.message
    );

    return Promise.reject(error);
  }
);


/* ===================== GL MANAGER API ===================== */

export const glManagerAPI = {
  /*
   * What is inside one product group: every flavour, split by pack size and by
   * site.
   *
   * The dashboards report a group as a single figure because a group is the
   * thing a manager sets up. The question that always follows is which of them
   * — which flavour carried the group, which is dead stock, and how many went
   * out as five-packs rather than singles.
   */
  getGroupBreakdown: (groupId, params = {}) =>
    withRetry429(() =>
      api.get(`/gl-manager/groups/${groupId}/breakdown`, {
        params: withNoCache(params),
      })
    ),

  /*
   * How much money is waiting in Smart Review, and how long it has waited.
   *
   * An unreviewed row is absent from every total. The age matters more than the
   * amount: five rows from this morning is normal, five from three weeks ago
   * means the reports have been quietly wrong for three weeks.
   */
  /*
   * Every Golden Line in scope, each opened to its flavours and its sites,
   * over any date range. One product across the company, one site across
   * every product, or both narrowed at once - the same rows asked about
   * from whichever direction the report needs.
   */
  reportsBreakdown: (params = {}) =>
    withRetry429(() =>
      api.get(`/gl-manager/reports/breakdown`, {
        params: withNoCache(params),
      })
    ),

  dashboardReviewQueue: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/review-queue", {
        params: withNoCache(params),
      })
    ),

  /** How often the matcher is right, measured from decisions already made. */
  dashboardMatchAccuracy: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/match-accuracy", {
        params: withNoCache(params),
      })
    ),


  /* ---------- Product groups ---------- */

  listGroups: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/groups", {
        params: withNoCache(params),
      })
    ),

  createGroup: (payload = {}) =>
    api.post("/gl-manager/groups", payload),

  suggestGroupIdentity: (payload = {}) =>
    withRetry429(() => api.post("/gl-manager/groups/suggest-identity", payload)),

  updateGroup: (groupId, payload = {}) =>
    api.put(`/gl-manager/groups/${sanitizeId(groupId)}`, payload),

  deleteGroup: (groupId) =>
    api.delete(`/gl-manager/groups/${sanitizeId(groupId)}`),

  setGroupActive: (groupId, isActive) =>
    api.put(`/gl-manager/groups/${sanitizeId(groupId)}`, {
      isActive,
    }),

  toggleGroupActive: (groupId, isActive) =>
    api.put(`/gl-manager/groups/${sanitizeId(groupId)}`, {
      isActive,
    }),

  /* ---------- Group detail ---------- */

  getGroupDetail: async (groupId) => {
    const id = sanitizeId(groupId);

    try {
      return await withRetry429(() =>
        api.get(`/gl-manager/groups/${id}/detail`, {
          params: withNoCache(),
        })
      );
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 405) {
        return withRetry429(() =>
          api.get(`/gl-manager/groups/${id}`, {
            params: withNoCache(),
          })
        );
      }

      throw err;
    }
  },

  getGroupProducts: (groupId, params = {}) =>
    withRetry429(() =>
      api.get(`/gl-manager/groups/${sanitizeId(groupId)}/products`, {
        params: withNoCache(params),
      })
    ),

  /* ---------- Sites / departments / catalog ---------- */

  listSites: () =>
    withRetry429(() =>
      api.get("/gl-manager/sites", {
        params: withNoCache(),
      })
    ),

  listDepartments: () =>
    withRetry429(() =>
      api.get("/gl-manager/departments", {
        params: withNoCache(),
      })
    ),

  groupCatalog: (department = "ALL") =>
    withRetry429(() =>
      api.get("/gl-manager/group-catalog", {
        params: withNoCache({ department }),
      })
    ),

  /* ---------- Old performance APIs ---------- */

  performance: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/performance", {
        params: withNoCache(params),
      })
    ),

  rankings: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/rankings", {
        params: withNoCache(params),
      })
    ),

  /* ---------- New BI dashboard APIs ---------- */

  dashboardSummary: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/summary", {
        params: withNoCache(params),
      })
    ),

  dashboardSiteCoverage: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/site-coverage", {
        params: withNoCache(params),
      })
    ),

  dashboardTrend: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/trend", {
        params: withNoCache(params),
      })
    ),

  dashboardRankings: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/rankings", {
        params: withNoCache(params),
      })
    ),

  dashboardSiteProducts: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/site-products", {
        params: withNoCache(params),
      })
    ),

  dashboardSiteMatrix: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/site-matrix", {
        params: withNoCache(params),
      })
    ),

  dashboardSiteMatrixExport: (params = {}) =>
    api.get("/gl-manager/dashboard/site-matrix/export", {
      params,
      responseType: "blob",
    }),

  dashboardDepartmentBreakdown: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/department-breakdown", {
        params: withNoCache(params),
      })
    ),

  dashboardCompareSites: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/compare-sites", {
        params: withNoCache(params),
      })
    ),

  dashboardProductDetail: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/product-detail", {
        params: withNoCache(params),
      })
    ),

  dashboardHeatmap: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/heatmap", {
        params: withNoCache(params),
      })
    ),

  dashboardProductSearch: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/dashboard/product-search", {
        params: withNoCache(params),
      })
    ),

  /* ---------- Site import profiles ---------- */

  getSiteImportProfiles: () =>
    withRetry429(() => api.get("/gl-manager/site-import-profiles", { params: withNoCache() })),

  getSiteImportProfile: (siteId) =>
    withRetry429(() =>
      api.get(`/gl-manager/site-import-profiles/${sanitizeId(siteId)}`, {
        params: withNoCache(),
      })
    ),

  updateSiteImportProfile: (siteId, payload = {}) =>
    api.put(`/gl-manager/site-import-profiles/${sanitizeId(siteId)}`, payload),

  /* ---------- Barcodes / SKUs ---------- */

  addGroupBarcodes: (groupId, barcodes = []) =>
    api.post(`/gl-manager/groups/${sanitizeId(groupId)}/skus`, {
      barcodes,
    }),

  upsertGroupSkus: (groupId, payload = {}) =>
    api.post(`/gl-manager/groups/${sanitizeId(groupId)}/skus`, payload),

  addGroupAliases: (groupId, aliases = []) =>
    api.post(`/gl-manager/groups/${sanitizeId(groupId)}/aliases`, { aliases }),

  updateGroupSku: (groupId, barcode, payload = {}) =>
    api.put(
      `/gl-manager/groups/${sanitizeId(groupId)}/skus/${encodeURIComponent(
        String(barcode || "").trim()
      )}`,
      payload
    ),

  deleteGroupSku: (groupId, barcode) =>
    api.delete(
      `/gl-manager/groups/${sanitizeId(groupId)}/skus/${encodeURIComponent(
        String(barcode || "").trim()
      )}`
    ),

 

  /* ---------- Smart Review ---------- */

  smartReview: (params = {}) =>
    withRetry429(() =>
      api.get("/gl-manager/smart-review", {
        params: withNoCache(params),
      })
    ),

  approveSmartReview: (id, payload = {}) =>
    api.post(`/gl-manager/smart-review/${sanitizeId(id)}/approve`, payload),

  rejectSmartReview: (id, payload = {}) =>
    api.post(`/gl-manager/smart-review/${sanitizeId(id)}/reject`, payload),

  markSmartReviewNotGoldenLine: (id, payload = {}) =>
    api.post(`/gl-manager/smart-review/${sanitizeId(id)}/not-golden-line`, payload),
};

/* =================== GL DIRECTORS API =================== */

export const glDirectorsAPI = {
  /*
   * What is inside one product group: every flavour, split by pack size and by
   * site.
   *
   * The dashboards report a group as a single figure because a group is the
   * thing a manager sets up. The question that always follows is which of them
   * — which flavour carried the group, which is dead stock, and how many went
   * out as five-packs rather than singles.
   */
  getGroupBreakdown: (groupId, params = {}) =>
    withRetry429(() =>
      directorApi.get(`/gl-manager/groups/${groupId}/breakdown`, {
        params: withNoCache(params),
      })
    ),

  /*
   * How much money is waiting in Smart Review, and how long it has waited.
   *
   * An unreviewed row is absent from every total. The age matters more than the
   * amount: five rows from this morning is normal, five from three weeks ago
   * means the reports have been quietly wrong for three weeks.
   */
  /*
   * Every Golden Line in scope, each opened to its flavours and its sites,
   * over any date range. One product across the company, one site across
   * every product, or both narrowed at once - the same rows asked about
   * from whichever direction the report needs.
   */
  reportsBreakdown: (params = {}) =>
    withRetry429(() =>
      directorApi.get(`/gl-manager/reports/breakdown`, {
        params: withNoCache(params),
      })
    ),

  dashboardReviewQueue: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/review-queue", {
        params: withNoCache(params),
      })
    ),

  /** How often the matcher is right, measured from decisions already made. */
  dashboardMatchAccuracy: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/match-accuracy", {
        params: withNoCache(params),
      })
    ),


    performance: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/performance", {
        params: withNoCache(params),
      })
    ),

  rankings: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/rankings", {
        params: withNoCache(params),
      })
    ),
  listSites: () =>
    withRetry429(() =>
      directorApi.get("/gl-manager/sites", {
        params: withNoCache(),
      })
    ),

  dashboardSummary: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/summary", {
        params: withNoCache(params),
      })
    ),

  dashboardSiteCoverage: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/site-coverage", {
        params: withNoCache(params),
      })
    ),

  dashboardTrend: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/trend", {
        params: withNoCache(params),
      })
    ),

  dashboardRankings: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/rankings", {
        params: withNoCache(params),
      })
    ),

  dashboardSiteProducts: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/site-products", {
        params: withNoCache(params),
      })
    ),

  dashboardSiteMatrix: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/site-matrix", {
        params: withNoCache(params),
      })
    ),

  dashboardSiteMatrixExport: (params = {}) =>
    directorApi.get("/gl-manager/dashboard/site-matrix/export", {
      params,
      responseType: "blob",
    }),

  dashboardDepartmentBreakdown: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/department-breakdown", {
        params: withNoCache(params),
      })
    ),

  dashboardCompareSites: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/compare-sites", {
        params: withNoCache(params),
      })
    ),

  dashboardProductDetail: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/product-detail", {
        params: withNoCache(params),
      })
    ),

  dashboardHeatmap: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/heatmap", {
        params: withNoCache(params),
      })
    ),

  dashboardProductSearch: (params = {}) =>
    withRetry429(() =>
      directorApi.get("/gl-manager/dashboard/product-search", {
        params: withNoCache(params),
      })
    ),
};

export default api;
